/**
 * Credential Vault Cryptography Utilities
 *
 * Zero-Knowledge Encryption Architecture:
 * - All encryption/decryption happens client-side
 * - Server NEVER sees plaintext credentials
 * - Uses AES-256-GCM for symmetric encryption
 * - Uses PBKDF2 with 600,000+ iterations for key derivation
 * - Each credential has unique IV and salt
 *
 * Hybrid Encryption (RSA + AES):
 * - RSA-2048 for asymmetric key wrapping (sub-user write-only access)
 * - AES-256-GCM for data encryption
 * - Sub-users can encrypt with public key but cannot decrypt without private key
 *
 * Security Features:
 * - AES-256-GCM provides authenticated encryption (confidentiality + integrity)
 * - PBKDF2 with high iteration count resists brute-force attacks
 * - Random IV ensures same plaintext produces different ciphertext
 * - Auth tag prevents tampering with encrypted data
 * - RSA-OAEP with SHA-256 for secure key wrapping
 */

// Constants
const ALGORITHM = "AES-GCM";
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits recommended for GCM
const SALT_LENGTH = 32; // 256 bits
const TAG_LENGTH = 128; // 128 bits
const DEFAULT_ITERATIONS = 600000; // OWASP recommended minimum

/**
 * Credential types
 */
export type CredentialType =
  | "password"
  | "api_key"
  | "ssh_key"
  | "certificate"
  | "database"
  | "server"
  | "secure_note"
  | "wifi"
  | "credit_card"
  | "custom";

/**
 * Credential entry structure (individual field within encrypted data)
 */
export interface CredentialEntry {
  key: string;
  value: string;
  type: "text" | "password" | "url" | "email" | "username";
}

/**
 * Custom field structure (for additional encrypted fields)
 */
export interface CustomField {
  name: string;
  encrypted_value: string;
  iv: string;
  auth_tag: string;
}

/**
 * Encrypted credential data structure
 */
export interface EncryptedCredential {
  encrypted_data: string; // Base64 encoded ciphertext
  iv: string; // Base64 encoded IV
  auth_tag: string; // Base64 encoded auth tag
  salt: string; // Base64 encoded salt
  key_iterations: number;
}

/**
 * Extended encrypted credential with metadata
 */
export interface EncryptedCredentialEntry extends EncryptedCredential {
  credential_type: CredentialType;
  url?: string;
  username?: string;
  encrypted_notes?: string;
  notes_iv?: string;
  notes_auth_tag?: string;
  custom_fields?: CustomField[];
  expires_at?: string;
  rotation_reminder_days?: number;
  strength_score?: number;
  is_favorite?: boolean;
}

/**
 * Decrypted credential data structure
 */
export interface DecryptedCredential {
  entries: CredentialEntry[];
}

/**
 * Convert ArrayBuffer to Base64 string
 */
export const arrayBufferToBase64 = (buffer: ArrayBuffer | ArrayBufferLike): string => {
  const bytes = new Uint8Array(buffer as ArrayBuffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Convert Base64 string to ArrayBuffer
 */
export const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer as ArrayBuffer;
};

/**
 * Generate cryptographically secure random bytes
 */
export const generateRandomBytes = (length: number): Uint8Array => {
  return crypto.getRandomValues(new Uint8Array(length));
};

/**
 * Derive encryption key from password using PBKDF2
 */
const deriveKey = async (
  password: string,
  salt: Uint8Array,
  iterations: number
): Promise<CryptoKey> => {
  // Import password as key material
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  // Derive AES-GCM key
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt.buffer as ArrayBuffer,
      iterations: iterations,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ["encrypt", "decrypt"]
  );
};

/**
 * Encrypt credential data
 *
 * @param data - The credential entries to encrypt
 * @param password - The encryption password (master password)
 * @param iterations - PBKDF2 iterations (default: 600000)
 * @param providedSaltBase64 - Optional pre-generated salt (base64). If provided, uses this instead of generating new one.
 *                             Useful for re-encryption with access password where all entries share the same salt.
 * @returns Encrypted credential object
 */
export const encryptCredential = async (
  data: DecryptedCredential,
  password: string,
  iterations: number = DEFAULT_ITERATIONS,
  providedSaltBase64?: string
): Promise<EncryptedCredential> => {
  // Generate random IV (always unique per encryption)
  const iv = generateRandomBytes(IV_LENGTH);

  // Use provided salt or generate new one
  const salt = providedSaltBase64
    ? new Uint8Array(base64ToArrayBuffer(providedSaltBase64))
    : generateRandomBytes(SALT_LENGTH);

  // Derive key from password
  const key = await deriveKey(password, salt, iterations);

  // Encrypt the data
  const plaintext = new TextEncoder().encode(JSON.stringify(data));
  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: iv.buffer as ArrayBuffer, tagLength: TAG_LENGTH },
    key,
    plaintext
  );

  // GCM mode appends auth tag to ciphertext
  // Extract ciphertext and auth tag
  const ciphertextArray = new Uint8Array(ciphertext);
  const tagStart = ciphertextArray.length - (TAG_LENGTH / 8);
  const encryptedData = ciphertextArray.slice(0, tagStart);
  const authTag = ciphertextArray.slice(tagStart);

  return {
    encrypted_data: arrayBufferToBase64(encryptedData.buffer),
    iv: arrayBufferToBase64(iv.buffer),
    auth_tag: arrayBufferToBase64(authTag.buffer),
    salt: arrayBufferToBase64(salt.buffer),
    key_iterations: iterations
  };
};

/**
 * Decrypt credential data
 *
 * @param encrypted - The encrypted credential object
 * @param password - The decryption password (master password)
 * @returns Decrypted credential data
 * @throws Error if decryption fails (wrong password or tampered data)
 */
export const decryptCredential = async (
  encrypted: EncryptedCredential,
  password: string
): Promise<DecryptedCredential> => {
  try {
    // Decode Base64 values
    const iv = new Uint8Array(base64ToArrayBuffer(encrypted.iv));
    const salt = new Uint8Array(base64ToArrayBuffer(encrypted.salt));
    const encryptedData = new Uint8Array(base64ToArrayBuffer(encrypted.encrypted_data));
    const authTag = new Uint8Array(base64ToArrayBuffer(encrypted.auth_tag));

    // Derive key from password
    const key = await deriveKey(password, salt, encrypted.key_iterations);

    // Reconstruct ciphertext with auth tag (GCM expects them together)
    const ciphertext = new Uint8Array(encryptedData.length + authTag.length);
    ciphertext.set(encryptedData);
    ciphertext.set(authTag, encryptedData.length);

    // Decrypt the data
    const plaintext = await crypto.subtle.decrypt(
      { name: ALGORITHM, iv: iv, tagLength: TAG_LENGTH },
      key,
      ciphertext
    );

    // Parse and return the decrypted data
    const decoded = new TextDecoder().decode(plaintext);
    return JSON.parse(decoded) as DecryptedCredential;
  } catch (error) {
    // Decryption failed - wrong password or tampered data
    throw new Error("Decryption failed. Please check your password.");
  }
};

/**
 * Validate password strength
 * Returns an object with validation results
 */
export const validatePasswordStrength = (password: string): {
  isValid: boolean;
  score: number;
  feedback: string[];
} => {
  const feedback: string[] = [];
  let score = 0;

  // Minimum length
  if (password.length >= 12) {
    score += 2;
  } else if (password.length >= 8) {
    score += 1;
    feedback.push("Consider using at least 12 characters");
  } else {
    feedback.push("Password must be at least 8 characters");
  }

  // Uppercase letters
  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push("Add uppercase letters");
  }

  // Lowercase letters
  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push("Add lowercase letters");
  }

  // Numbers
  if (/[0-9]/.test(password)) {
    score += 1;
  } else {
    feedback.push("Add numbers");
  }

  // Special characters
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    score += 2;
  } else {
    feedback.push("Add special characters (!@#$%^&*...)");
  }

  // Length bonus
  if (password.length >= 16) {
    score += 1;
  }

  return {
    isValid: score >= 5 && password.length >= 8,
    score: Math.min(score, 8), // Max score of 8
    feedback
  };
};

/**
 * Password generation options
 */
export interface PasswordGeneratorOptions {
  length: number;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
  excludeAmbiguous: boolean; // Exclude 0, O, l, 1, I
  excludeSimilar: boolean; // Exclude characters that look similar
  customSymbols?: string;
  minUppercase?: number;
  minLowercase?: number;
  minNumbers?: number;
  minSymbols?: number;
}

const DEFAULT_PASSWORD_OPTIONS: PasswordGeneratorOptions = {
  length: 16,
  includeUppercase: true,
  includeLowercase: true,
  includeNumbers: true,
  includeSymbols: true,
  excludeAmbiguous: false,
  excludeSimilar: false
};

// Character sets
const UPPERCASE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWERCASE = "abcdefghijklmnopqrstuvwxyz";
const NUMBERS = "0123456789";
const SYMBOLS = "!@#$%^&*()_+-=[]{}|;:,.<>?";
const AMBIGUOUS_CHARS = "0O1lI";
const SIMILAR_CHARS = "0O1lI{}[]()\"'`";

/**
 * Generate a secure random password with customizable options
 */
export const generateSecurePassword = (
  optionsOrLength: number | Partial<PasswordGeneratorOptions> = 16
): string => {
  // Handle legacy call with just length
  const options: PasswordGeneratorOptions = typeof optionsOrLength === "number"
    ? { ...DEFAULT_PASSWORD_OPTIONS, length: optionsOrLength }
    : { ...DEFAULT_PASSWORD_OPTIONS, ...optionsOrLength };

  // Build character set based on options
  let charset = "";
  let requiredChars: string[] = [];

  if (options.includeLowercase) {
    let chars = LOWERCASE;
    if (options.excludeAmbiguous) chars = chars.replace(new RegExp(`[${AMBIGUOUS_CHARS}]`, "gi"), "");
    if (options.excludeSimilar) chars = chars.replace(new RegExp(`[${SIMILAR_CHARS}]`, "g"), "");
    charset += chars;

    // Add minimum required characters
    if (options.minLowercase && chars.length > 0) {
      for (let i = 0; i < options.minLowercase; i++) {
        requiredChars.push(chars[Math.floor(Math.random() * chars.length)]);
      }
    }
  }

  if (options.includeUppercase) {
    let chars = UPPERCASE;
    if (options.excludeAmbiguous) chars = chars.replace(new RegExp(`[${AMBIGUOUS_CHARS}]`, "gi"), "");
    if (options.excludeSimilar) chars = chars.replace(new RegExp(`[${SIMILAR_CHARS}]`, "g"), "");
    charset += chars;

    if (options.minUppercase && chars.length > 0) {
      for (let i = 0; i < options.minUppercase; i++) {
        requiredChars.push(chars[Math.floor(Math.random() * chars.length)]);
      }
    }
  }

  if (options.includeNumbers) {
    let chars = NUMBERS;
    if (options.excludeAmbiguous) chars = chars.replace(/[01]/g, "");
    charset += chars;

    if (options.minNumbers && chars.length > 0) {
      for (let i = 0; i < options.minNumbers; i++) {
        requiredChars.push(chars[Math.floor(Math.random() * chars.length)]);
      }
    }
  }

  if (options.includeSymbols) {
    let chars = options.customSymbols || SYMBOLS;
    if (options.excludeSimilar) chars = chars.replace(new RegExp(`[${SIMILAR_CHARS}]`, "g"), "");
    charset += chars;

    if (options.minSymbols && chars.length > 0) {
      for (let i = 0; i < options.minSymbols; i++) {
        requiredChars.push(chars[Math.floor(Math.random() * chars.length)]);
      }
    }
  }

  // Fallback if no character types selected
  if (charset.length === 0) {
    charset = LOWERCASE + UPPERCASE + NUMBERS;
  }

  // Generate remaining random characters
  const remainingLength = Math.max(0, options.length - requiredChars.length);
  const randomValues = generateRandomBytes(remainingLength);

  let passwordChars = [...requiredChars];
  for (let i = 0; i < remainingLength; i++) {
    passwordChars.push(charset[randomValues[i] % charset.length]);
  }

  // Shuffle the password characters
  for (let i = passwordChars.length - 1; i > 0; i--) {
    const randomByte = generateRandomBytes(1)[0];
    const j = randomByte % (i + 1);
    [passwordChars[i], passwordChars[j]] = [passwordChars[j], passwordChars[i]];
  }

  return passwordChars.join("");
};

/**
 * Calculate password strength score (0-100)
 */
export const calculatePasswordStrength = (password: string): number => {
  if (!password) return 0;

  let score = 0;
  const length = password.length;

  // Length scoring (up to 30 points)
  if (length >= 8) score += 10;
  if (length >= 12) score += 10;
  if (length >= 16) score += 5;
  if (length >= 20) score += 5;

  // Character variety (up to 40 points)
  if (/[a-z]/.test(password)) score += 10;
  if (/[A-Z]/.test(password)) score += 10;
  if (/[0-9]/.test(password)) score += 10;
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score += 10;

  // Bonus for mixed character types (up to 20 points)
  const types = [
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
  ].filter(Boolean).length;
  score += (types - 1) * 5;

  // Penalty for common patterns
  if (/^[a-zA-Z]+$/.test(password)) score -= 10; // Only letters
  if (/^[0-9]+$/.test(password)) score -= 20; // Only numbers
  if (/(.)\1{2,}/.test(password)) score -= 10; // Repeated characters
  if (/^(123|abc|qwerty)/i.test(password)) score -= 15; // Common sequences

  // Entropy bonus
  const uniqueChars = new Set(password).size;
  score += Math.min(10, Math.floor(uniqueChars / 2));

  return Math.max(0, Math.min(100, score));
};

/**
 * Encrypt notes field separately
 */
export const encryptNotes = async (
  notes: string,
  password: string,
  salt: Uint8Array,
  iterations: number = DEFAULT_ITERATIONS
): Promise<{ encrypted_notes: string; notes_iv: string; notes_auth_tag: string } | null> => {
  if (!notes || notes.trim() === "") return null;

  const iv = generateRandomBytes(IV_LENGTH);
  const key = await deriveKey(password, salt, iterations);

  const plaintext = new TextEncoder().encode(notes);
  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: iv.buffer as ArrayBuffer, tagLength: TAG_LENGTH },
    key,
    plaintext
  );

  const ciphertextArray = new Uint8Array(ciphertext);
  const tagStart = ciphertextArray.length - (TAG_LENGTH / 8);
  const encryptedData = ciphertextArray.slice(0, tagStart);
  const authTag = ciphertextArray.slice(tagStart);

  return {
    encrypted_notes: arrayBufferToBase64(encryptedData.buffer),
    notes_iv: arrayBufferToBase64(iv.buffer),
    notes_auth_tag: arrayBufferToBase64(authTag.buffer)
  };
};

/**
 * Decrypt notes field
 */
export const decryptNotes = async (
  encrypted_notes: string,
  notes_iv: string,
  notes_auth_tag: string,
  password: string,
  salt: string,
  iterations: number
): Promise<string> => {
  const iv = new Uint8Array(base64ToArrayBuffer(notes_iv));
  const saltArray = new Uint8Array(base64ToArrayBuffer(salt));
  const encryptedData = new Uint8Array(base64ToArrayBuffer(encrypted_notes));
  const authTag = new Uint8Array(base64ToArrayBuffer(notes_auth_tag));

  const key = await deriveKey(password, saltArray, iterations);

  const ciphertext = new Uint8Array(encryptedData.length + authTag.length);
  ciphertext.set(encryptedData);
  ciphertext.set(authTag, encryptedData.length);

  const plaintext = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv: iv, tagLength: TAG_LENGTH },
    key,
    ciphertext
  );

  return new TextDecoder().decode(plaintext);
};

/**
 * Encrypt a custom field
 */
export const encryptCustomField = async (
  name: string,
  value: string,
  password: string,
  salt: Uint8Array,
  iterations: number = DEFAULT_ITERATIONS
): Promise<CustomField> => {
  const iv = generateRandomBytes(IV_LENGTH);
  const key = await deriveKey(password, salt, iterations);

  const plaintext = new TextEncoder().encode(value);
  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: iv.buffer as ArrayBuffer, tagLength: TAG_LENGTH },
    key,
    plaintext
  );

  const ciphertextArray = new Uint8Array(ciphertext);
  const tagStart = ciphertextArray.length - (TAG_LENGTH / 8);
  const encryptedData = ciphertextArray.slice(0, tagStart);
  const authTag = ciphertextArray.slice(tagStart);

  return {
    name,
    encrypted_value: arrayBufferToBase64(encryptedData.buffer),
    iv: arrayBufferToBase64(iv.buffer),
    auth_tag: arrayBufferToBase64(authTag.buffer)
  };
};

/**
 * Decrypt a custom field
 */
export const decryptCustomField = async (
  field: CustomField,
  password: string,
  salt: string,
  iterations: number
): Promise<{ name: string; value: string }> => {
  const iv = new Uint8Array(base64ToArrayBuffer(field.iv));
  const saltArray = new Uint8Array(base64ToArrayBuffer(salt));
  const encryptedData = new Uint8Array(base64ToArrayBuffer(field.encrypted_value));
  const authTag = new Uint8Array(base64ToArrayBuffer(field.auth_tag));

  const key = await deriveKey(password, saltArray, iterations);

  const ciphertext = new Uint8Array(encryptedData.length + authTag.length);
  ciphertext.set(encryptedData);
  ciphertext.set(authTag, encryptedData.length);

  const plaintext = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv: iv, tagLength: TAG_LENGTH },
    key,
    ciphertext
  );

  return {
    name: field.name,
    value: new TextDecoder().decode(plaintext)
  };
};

// ============================================================================
// RSA ASYMMETRIC ENCRYPTION UTILITIES
// For sub-user write-only access (can encrypt, cannot decrypt)
// ============================================================================

/**
 * RSA key pair for hybrid encryption
 */
export interface RSAKeyPair {
  publicKey: string; // PEM format
  privateKey: string; // PEM format
}

/**
 * Encrypted private key structure
 */
export interface EncryptedPrivateKey {
  encrypted_private_key: string; // Base64 encoded
  private_key_iv: string; // Base64 encoded
  private_key_salt: string; // Base64 encoded
  private_key_auth_tag: string; // Base64 encoded
}

/**
 * Hybrid encrypted data structure (RSA + AES)
 */
export interface HybridEncryptedData {
  encrypted_data: string; // AES encrypted data (Base64)
  iv: string; // AES IV (Base64)
  auth_tag: string; // AES auth tag (Base64)
  encrypted_aes_key: string; // RSA encrypted AES key (Base64)
  encrypted_notes?: string; // AES encrypted notes (Base64)
  notes_iv?: string; // AES notes IV (Base64)
  notes_auth_tag?: string; // AES notes auth tag (Base64)
}

/**
 * Convert ArrayBuffer to PEM format
 */
const arrayBufferToPem = (buffer: ArrayBuffer, type: "PUBLIC" | "PRIVATE"): string => {
  const base64 = arrayBufferToBase64(buffer);
  const lines = base64.match(/.{1,64}/g) || [];
  return `-----BEGIN ${type} KEY-----\n${lines.join("\n")}\n-----END ${type} KEY-----`;
};

/**
 * Convert PEM format to ArrayBuffer
 */
const pemToArrayBuffer = (pem: string): ArrayBuffer => {
  const base64 = pem
    .replace(/-----BEGIN [A-Z]+ KEY-----/, "")
    .replace(/-----END [A-Z]+ KEY-----/, "")
    .replace(/\s/g, "");
  return base64ToArrayBuffer(base64);
};

/**
 * Generate RSA-2048 key pair for hybrid encryption
 *
 * @returns RSA key pair in PEM format
 */
export const generateRSAKeyPair = async (): Promise<RSAKeyPair> => {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]), // 65537
      hash: "SHA-256"
    },
    true, // extractable
    ["encrypt", "decrypt"]
  );

  // Export public key
  const publicKeyBuffer = await crypto.subtle.exportKey("spki", keyPair.publicKey);
  const publicKey = arrayBufferToPem(publicKeyBuffer, "PUBLIC");

  // Export private key
  const privateKeyBuffer = await crypto.subtle.exportKey("pkcs8", keyPair.privateKey);
  const privateKey = arrayBufferToPem(privateKeyBuffer, "PRIVATE");

  return { publicKey, privateKey };
};

/**
 * Encrypt private key with master password for secure storage
 *
 * @param privateKey - RSA private key in PEM format
 * @param masterPassword - User's master password
 * @returns Encrypted private key data
 */
export const encryptPrivateKey = async (
  privateKey: string,
  masterPassword: string
): Promise<EncryptedPrivateKey> => {
  const iv = generateRandomBytes(IV_LENGTH);
  const salt = generateRandomBytes(SALT_LENGTH);
  const key = await deriveKey(masterPassword, salt, DEFAULT_ITERATIONS);

  const plaintext = new TextEncoder().encode(privateKey);
  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: iv.buffer as ArrayBuffer, tagLength: TAG_LENGTH },
    key,
    plaintext
  );

  const ciphertextArray = new Uint8Array(ciphertext);
  const tagStart = ciphertextArray.length - (TAG_LENGTH / 8);
  const encryptedData = ciphertextArray.slice(0, tagStart);
  const authTag = ciphertextArray.slice(tagStart);

  return {
    encrypted_private_key: arrayBufferToBase64(encryptedData.buffer),
    private_key_iv: arrayBufferToBase64(iv.buffer),
    private_key_salt: arrayBufferToBase64(salt.buffer),
    private_key_auth_tag: arrayBufferToBase64(authTag.buffer)
  };
};

/**
 * Decrypt private key with master password
 *
 * @param encryptedKey - Encrypted private key data
 * @param masterPassword - User's master password
 * @returns Decrypted RSA private key in PEM format
 */
export const decryptPrivateKey = async (
  encryptedKey: EncryptedPrivateKey,
  masterPassword: string
): Promise<string> => {
  const iv = new Uint8Array(base64ToArrayBuffer(encryptedKey.private_key_iv));
  const salt = new Uint8Array(base64ToArrayBuffer(encryptedKey.private_key_salt));
  const encryptedData = new Uint8Array(base64ToArrayBuffer(encryptedKey.encrypted_private_key));
  const authTag = new Uint8Array(base64ToArrayBuffer(encryptedKey.private_key_auth_tag));

  const key = await deriveKey(masterPassword, salt, DEFAULT_ITERATIONS);

  const ciphertext = new Uint8Array(encryptedData.length + authTag.length);
  ciphertext.set(encryptedData);
  ciphertext.set(authTag, encryptedData.length);

  const plaintext = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv: iv, tagLength: TAG_LENGTH },
    key,
    ciphertext
  );

  return new TextDecoder().decode(plaintext);
};


/**
 * Import RSA public key from PEM format
 */
const importPublicKey = async (publicKeyPem: string): Promise<CryptoKey> => {
  const keyBuffer = pemToArrayBuffer(publicKeyPem);
  return crypto.subtle.importKey(
    "spki",
    keyBuffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
};

/**
 * Import RSA private key from PEM format
 */
const importPrivateKey = async (privateKeyPem: string): Promise<CryptoKey> => {
  const keyBuffer = pemToArrayBuffer(privateKeyPem);
  return crypto.subtle.importKey(
    "pkcs8",
    keyBuffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["decrypt"]
  );
};

/**
 * Encrypt data using hybrid encryption (RSA + AES)
 *
 * This is used by sub-users who only have the public key.
 * They can encrypt credentials but cannot decrypt them.
 *
 * Flow:
 * 1. Generate random AES key
 * 2. Encrypt data with AES key
 * 3. Encrypt AES key with RSA public key
 *
 * @param data - Data to encrypt
 * @param publicKeyPem - RSA public key in PEM format
 * @returns Hybrid encrypted data
 */
export const encryptWithPublicKey = async (
  data: DecryptedCredential,
  publicKeyPem: string,
  notes?: string
): Promise<HybridEncryptedData> => {
  // Generate random AES key
  const aesKey = await crypto.subtle.generateKey(
    { name: ALGORITHM, length: KEY_LENGTH },
    true, // extractable (we need to encrypt it with RSA)
    ["encrypt"]
  );

  // Generate random IV
  const iv = generateRandomBytes(IV_LENGTH);

  // Encrypt data with AES
  const plaintext = new TextEncoder().encode(JSON.stringify(data));
  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: iv.buffer as ArrayBuffer, tagLength: TAG_LENGTH },
    aesKey,
    plaintext
  );

  // Extract ciphertext and auth tag
  const ciphertextArray = new Uint8Array(ciphertext);
  const tagStart = ciphertextArray.length - (TAG_LENGTH / 8);
  const encryptedData = ciphertextArray.slice(0, tagStart);
  const authTag = ciphertextArray.slice(tagStart);

  // Export AES key as raw bytes
  const aesKeyRaw = await crypto.subtle.exportKey("raw", aesKey);

  // Encrypt AES key with RSA public key
  const publicKey = await importPublicKey(publicKeyPem);
  const encryptedAesKey = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    aesKeyRaw
  );

  // Encrypt notes if provided
  let encrypted_notes: string | undefined;
  let notes_iv: string | undefined;
  let notes_auth_tag: string | undefined;

  if (notes && notes.trim() !== "") {
    const nIv = generateRandomBytes(IV_LENGTH);
    const nPlaintext = new TextEncoder().encode(notes);
    const nCiphertext = await crypto.subtle.encrypt(
      { name: ALGORITHM, iv: nIv.buffer as ArrayBuffer, tagLength: TAG_LENGTH },
      aesKey,
      nPlaintext
    );

    const nCiphertextArray = new Uint8Array(nCiphertext);
    const nTagStart = nCiphertextArray.length - (TAG_LENGTH / 8);
    encrypted_notes = arrayBufferToBase64(nCiphertextArray.slice(0, nTagStart).buffer);
    notes_iv = arrayBufferToBase64(nIv.buffer);
    notes_auth_tag = arrayBufferToBase64(nCiphertextArray.slice(nTagStart).buffer);
  }

  return {
    encrypted_data: arrayBufferToBase64(encryptedData.buffer),
    iv: arrayBufferToBase64(iv.buffer),
    auth_tag: arrayBufferToBase64(authTag.buffer),
    encrypted_aes_key: arrayBufferToBase64(encryptedAesKey),
    encrypted_notes,
    notes_iv,
    notes_auth_tag
  };
};

/**
 * Unified decryption function for notes that handles both encryption modes
 */
export const decryptNotesUnified = async (
  entry: {
    encrypted_notes?: string;
    notes_iv?: string;
    notes_auth_tag?: string;
    salt?: string | null;
    key_iterations?: number | null;
    encrypted_credential_key?: string | null;
  },
  masterPassword: string,
  vault?: VaultWithKeys
): Promise<string> => {
  if (!entry.encrypted_notes || !entry.notes_iv || !entry.notes_auth_tag) {
    return "";
  }

  const isPublicKeyMode = !entry.salt && entry.encrypted_credential_key;

  if (isPublicKeyMode) {
    if (!vault?.encrypted_private_key || !vault?.private_key_iv ||
        !vault?.private_key_salt || !vault?.private_key_auth_tag) {
      throw new Error("Vault encryption keys not available for public key mode");
    }

    const privateKeyPem = await decryptPrivateKey({
      encrypted_private_key: vault.encrypted_private_key,
      private_key_iv: vault.private_key_iv,
      private_key_salt: vault.private_key_salt,
      private_key_auth_tag: vault.private_key_auth_tag
    }, masterPassword);

    return decryptNotesHybrid(
      entry.encrypted_notes,
      entry.notes_iv,
      entry.notes_auth_tag,
      entry.encrypted_credential_key!,
      privateKeyPem
    );
  } else {
    if (!entry.salt) {
      throw new Error("Missing salt for password-encrypted entries");
    }
    return decryptNotes(
      entry.encrypted_notes,
      entry.notes_iv,
      entry.notes_auth_tag,
      masterPassword,
      entry.salt,
      entry.key_iterations || DEFAULT_ITERATIONS
    );
  }
};

/**
 * Decrypt data using hybrid encryption (RSA + AES)
 *
 * This is used by the vault owner who has the private key.
 *
 * Flow:
 * 1. Decrypt AES key with RSA private key
 * 2. Decrypt data with AES key
 *
 * @param encryptedData - Hybrid encrypted data
 * @param privateKeyPem - RSA private key in PEM format
 * @returns Decrypted credential data
 */
export const decryptWithPrivateKey = async (
  encryptedData: HybridEncryptedData,
  privateKeyPem: string
): Promise<DecryptedCredential> => {
  // Import private key
  const privateKey = await importPrivateKey(privateKeyPem);

  // Decrypt AES key with RSA private key
  const encryptedAesKey = base64ToArrayBuffer(encryptedData.encrypted_aes_key);
  const aesKeyRaw = await crypto.subtle.decrypt(
    { name: "RSA-OAEP" },
    privateKey,
    encryptedAesKey
  );

  // Import AES key
  const aesKey = await crypto.subtle.importKey(
    "raw",
    aesKeyRaw,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ["decrypt"]
  );

  // Decode encrypted data components
  const iv = new Uint8Array(base64ToArrayBuffer(encryptedData.iv));
  const ciphertextData = new Uint8Array(base64ToArrayBuffer(encryptedData.encrypted_data));
  const authTag = new Uint8Array(base64ToArrayBuffer(encryptedData.auth_tag));

  // Reconstruct ciphertext with auth tag
  const ciphertext = new Uint8Array(ciphertextData.length + authTag.length);
  ciphertext.set(ciphertextData);
  ciphertext.set(authTag, ciphertextData.length);

  // Decrypt data with AES
  const plaintext = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv: iv, tagLength: TAG_LENGTH },
    aesKey,
    ciphertext
  );

  return JSON.parse(new TextDecoder().decode(plaintext)) as DecryptedCredential;
};

/**
 * Decrypt notes using hybrid encryption (RSA + AES)
 */
export const decryptNotesHybrid = async (
  encrypted_notes: string,
  notes_iv: string,
  notes_auth_tag: string,
  encrypted_aes_key: string,
  privateKeyPem: string
): Promise<string> => {
  // Import private key
  const privateKey = await importPrivateKey(privateKeyPem);

  // Decrypt AES key with RSA private key
  const aesKeyRaw = await crypto.subtle.decrypt(
    { name: "RSA-OAEP" },
    privateKey,
    base64ToArrayBuffer(encrypted_aes_key)
  );

  // Import AES key
  const aesKey = await crypto.subtle.importKey(
    "raw",
    aesKeyRaw,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ["decrypt"]
  );

  // Decode notes components
  const iv = new Uint8Array(base64ToArrayBuffer(notes_iv));
  const encryptedData = new Uint8Array(base64ToArrayBuffer(encrypted_notes));
  const authTag = new Uint8Array(base64ToArrayBuffer(notes_auth_tag));

  // Reconstruct ciphertext with auth tag
  const ciphertext = new Uint8Array(encryptedData.length + authTag.length);
  ciphertext.set(encryptedData);
  ciphertext.set(authTag, encryptedData.length);

  // Decrypt notes with AES
  const plaintext = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv: iv, tagLength: TAG_LENGTH },
    aesKey,
    ciphertext
  );

  return new TextDecoder().decode(plaintext);
};

/**
 * Re-encrypt credential from hybrid format to password-based format
 *
 * Used when business owner wants to share credentials with a program.
 * Owner decrypts with private key, then re-encrypts with access password.
 *
 * @param hybridData - Hybrid encrypted data (from sub-user)
 * @param privateKeyPem - Owner's RSA private key
 * @param accessPassword - Access password for the program
 * @returns Password-encrypted credential
 */
export const reEncryptForProgram = async (
  hybridData: HybridEncryptedData,
  privateKeyPem: string,
  accessPassword: string
): Promise<EncryptedCredential> => {
  // Decrypt with private key
  const decrypted = await decryptWithPrivateKey(hybridData, privateKeyPem);

  // Re-encrypt with access password
  return encryptCredential(decrypted, accessPassword);
};

/**
 * Database credential entry structure (from API)
 */
export interface DatabaseCredentialEntry {
  entry_id: number;
  encrypted_data: string;
  iv: string;
  auth_tag: string;
  salt: string | null;  // Null for public key encryption
  key_iterations: number | null;  // Null for public key encryption
  encrypted_credential_key?: string | null;  // Present for public key encryption
}

/**
 * Vault structure with encryption keys
 */
export interface VaultWithKeys {
  encrypted_private_key?: string;
  private_key_iv?: string;
  private_key_salt?: string;
  private_key_auth_tag?: string;
}

/**
 * Unified decryption function that handles both encryption modes:
 * 1. Master Password Mode (salt present): Use PBKDF2 to derive key
 * 2. Public Key Mode (encrypted_credential_key present): Use RSA to decrypt AES key
 *
 * @param entry - The encrypted credential entry from database
 * @param masterPassword - User's master password
 * @param vault - Vault containing encrypted private key (needed for public key mode)
 * @returns Decrypted credential data
 */
export const decryptCredentialUnified = async (
  entry: DatabaseCredentialEntry,
  masterPassword: string,
  vault?: VaultWithKeys
): Promise<DecryptedCredential> => {
  // Check which encryption mode was used
  const isPublicKeyMode = !entry.salt && entry.encrypted_credential_key;

  if (isPublicKeyMode) {
    // Public Key Mode: Decrypt AES key with RSA private key
    if (!vault?.encrypted_private_key || !vault?.private_key_iv ||
        !vault?.private_key_salt || !vault?.private_key_auth_tag) {
      throw new Error("Vault encryption keys not available for public key mode");
    }

    if (!entry.encrypted_credential_key) {
      throw new Error("Missing encrypted_credential_key for public key encrypted entry");
    }

    // First, decrypt the vault's private key using master password
    const privateKeyPem = await decryptPrivateKey({
      encrypted_private_key: vault.encrypted_private_key,
      private_key_iv: vault.private_key_iv,
      private_key_salt: vault.private_key_salt,
      private_key_auth_tag: vault.private_key_auth_tag
    }, masterPassword);

    // Then use the private key to decrypt the credential
    return decryptWithPrivateKey({
      encrypted_data: entry.encrypted_data,
      iv: entry.iv,
      auth_tag: entry.auth_tag,
      encrypted_aes_key: entry.encrypted_credential_key
    }, privateKeyPem);
  } else {
    // Master Password Mode: Use PBKDF2 to derive key from password + salt
    if (!entry.salt) {
      throw new Error("Missing salt for password-encrypted entry");
    }

    return decryptCredential({
      encrypted_data: entry.encrypted_data,
      iv: entry.iv,
      auth_tag: entry.auth_tag,
      salt: entry.salt,
      key_iterations: entry.key_iterations || DEFAULT_ITERATIONS
    }, masterPassword);
  }
};

/**
 * Check if an entry uses public key encryption
 */
export const isPublicKeyEncrypted = (entry: DatabaseCredentialEntry): boolean => {
  return !entry.salt && !!entry.encrypted_credential_key;
};
